import { ISelectOption } from '@aon/aon-tws-common-ui';
import { createAsyncThunk, createSelector, createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import axios from 'axios';
import { RootState } from '../appStore';
import {
	ActionType,
	AudienceSourceType,
	ConditionOperator,
	ConditionSourceType,
	ConditionType,
	DisplayLogicConditionError,
	ElementType,
	IDisplayLogicCondition,
	IDisplayLogicEditModel,
	IDisplayLogicQuestion,
	IPageFlowElementModel,
	IValidationModel,
	QuestionType,
	TargetValueOperator,
	TargetValueType,
} from '../models';
import * as QuestionBranchingLogicAPI from '../services/QuestionDisplayLogicAPI';
import { assertNonNullable } from '../utilities/assertions';
import { IApplicationState } from './';

interface IQuestionBranchingLogicState {
	isLoadingOrSaving: boolean;
	unsavedBranchingLogicChanges: boolean;
	branchingLogicEditModels: IDisplayLogicEditModel[];
	validationModels?: IValidationModel<IDisplayLogicEditModel>[];
}

const initialState: IQuestionBranchingLogicState = {
	isLoadingOrSaving: false,
	unsavedBranchingLogicChanges: false,
	branchingLogicEditModels: [],
};

const loadQuestionBranchingLogic = createAsyncThunk(
	'questionBranchingLogic/loadQuestionBranchingLogic',
	({ surveyId, questionId }: { surveyId: number; questionId: number }) =>
		QuestionBranchingLogicAPI.GetQuestionDisplayLogic(surveyId, questionId).then((result) => result[questionId] ?? []),
);

const saveQuestionBranchingLogic = createAsyncThunk(
	'questionBranchingLogic/saveQuestionBranchingLogic',
	({ surveyId, questionId }: { surveyId: number; questionId: number }, { getState, rejectWithValue }) => {
		const {
			questionBranchingLogic: { branchingLogicEditModels },
		} = getState() as IApplicationState;

		return QuestionBranchingLogicAPI.UpdateQuestionDisplayLogic(surveyId, {
			[questionId]: branchingLogicEditModels,
		}).catch((error) => {
			if (!axios.isAxiosError(error)) {
				return rejectWithValue(undefined);
			}

			const payload =
				!!error.response?.data && error.response.data[questionId].some((v: IValidationModel<IDisplayLogicEditModel>) => !v.isValid)
					? error.response.data[questionId]
					: undefined;
			return rejectWithValue(payload);
		});
	},
);

const saveGroupQuestionBranchingLogic = createAsyncThunk(
	'questionBranchingLogic/saveGroupQuestionBranchingLogic',
	async ({ surveyId, questionIds }: { surveyId: number; questionIds: number[] }, { getState, rejectWithValue }) => {
		const {
			questionBranchingLogic: { branchingLogicEditModels },
		} = getState() as IApplicationState;

		const request = questionIds.reduce((result, questionId) => {
			result[questionId] = branchingLogicEditModels.map((branchingLogicEditModel) => ({
				...branchingLogicEditModel,
				displayLogicRule: {
					...branchingLogicEditModel.displayLogicRule,
					surveyQuestionId: questionId,
				},
			}));
			return result;
		}, {});

		try {
			return await QuestionBranchingLogicAPI.CreateQuestionDisplayLogic(surveyId, request);
		} catch (error) {
			if (!axios.isAxiosError(error)) {
				return rejectWithValue(undefined);
			}

			const payload =
				!!error.response?.data && error.response.data[questionIds[0]].some((v: IValidationModel<IDisplayLogicEditModel>) => !v.isValid)
					? error.response.data[questionIds[0]]
					: undefined;
			return rejectWithValue(payload);
		}
	},
);

const questionBranchingLogicSlice = createSlice({
	name: 'questionBranchingLogic',
	initialState: initialState,
	reducers: {
		addNewRule: (
			state,
			{
				payload: { conditionSourceType, actionType, destinationQuestionId, questionId },
			}: PayloadAction<{
				actionType: ActionType;
				destinationQuestionId?: number;
				conditionSourceType: ConditionSourceType;
				questionId: number;
			}>,
		) => {
			const branchingLogicEditModel: IDisplayLogicEditModel = {
				displayLogicConditions: [],
				displayLogicRule: {
					surveyQuestionId: questionId,
					displayLogicAction: {
						actionType: actionType,
						destinationQuestionId,
					},
					conditionOperator: ConditionOperator.And,
				},
			};

			const condition = createNewCondition(0, conditionSourceType);

			branchingLogicEditModel.displayLogicConditions = [condition];
			const { branchingLogicEditModels } = state;

			branchingLogicEditModels.push(branchingLogicEditModel);
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		addNewCondition: (
			state,
			action: PayloadAction<{
				conditionSourceType: ConditionSourceType;
				branchingLogicIndex: number;
			}>,
		) => {
			const {
				payload: { conditionSourceType, branchingLogicIndex },
			} = action;

			const { branchingLogicEditModels } = state;
			const branchingLogicEditModel = branchingLogicEditModels[branchingLogicIndex];

			const conditionIndex = branchingLogicEditModel.displayLogicConditions.length;
			const condition = createNewCondition(conditionIndex, conditionSourceType);

			branchingLogicEditModel.displayLogicConditions.push(condition);
			branchingLogicEditModels[branchingLogicIndex] = branchingLogicEditModel;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		deleteCondition: (
			state,
			action: PayloadAction<{
				conditionIndex: number;
				branchingLogicIndex: number;
			}>,
		) => {
			const {
				payload: { conditionIndex, branchingLogicIndex },
			} = action;

			const { branchingLogicEditModels } = state;
			const branchingLogicEditModel = branchingLogicEditModels[branchingLogicIndex];

			branchingLogicEditModel.displayLogicConditions.splice(conditionIndex, 1);

			// update the indexes for the remaining conditions
			branchingLogicEditModel.displayLogicConditions.forEach((c, index) => (c.index = index));

			if (branchingLogicEditModel.displayLogicConditions.length === 0) {
				branchingLogicEditModels.splice(branchingLogicIndex, 1);
			} else {
				branchingLogicEditModels[branchingLogicIndex] = branchingLogicEditModel;
			}
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		attributeChange: (
			state,
			action: PayloadAction<{
				conditionIndex: number;
				branchingLogicIndex: number;
				attributeName: string;
				selectedAttributeId?: string;
			}>,
		) => {
			const {
				payload: { attributeName, conditionIndex, branchingLogicIndex, selectedAttributeId },
			} = action;
			const condition = getCondition(state, conditionIndex, branchingLogicIndex);

			condition.displayLogicValues = [];
			condition.conditionSourceId = selectedAttributeId?.toString();
			condition.conditionSourceText = attributeName;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		conditionQuestionChange: (
			state,
			action: PayloadAction<{
				conditionIndex: number;
				branchingLogicIndex: number;
				questionId: number;
				questionText: string;
			}>,
		) => {
			const {
				payload: { conditionIndex, branchingLogicIndex, questionId, questionText },
			} = action;

			const condition = getCondition(state, conditionIndex, branchingLogicIndex);
			condition.displayLogicValues = [];
			condition.conditionSourceId = questionId.toString();
			condition.conditionSourceText = questionText;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		conditionResponseChange: (
			state,
			action: PayloadAction<{
				conditionIndex: number;
				branchingLogicIndex: number;
				responses: string[];
			}>,
		) => {
			const {
				payload: { conditionIndex, branchingLogicIndex, responses },
			} = action;

			const condition = getCondition(state, conditionIndex, branchingLogicIndex);

			condition.displayLogicValues = responses.map((r) => ({
				targetValueType: TargetValueType.Response,
				valueText: r,
			}));
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		resetQuestionBranchingLogic: () => {
			return { ...initialState };
		},
		selectionDismissal: (
			state,
			action: PayloadAction<{
				conditionIndex: number;
				branchingLogicIndex: number;
				values: ISelectOption[];
			}>,
		) => {
			const {
				payload: { conditionIndex, branchingLogicIndex, values },
			} = action;

			const condition = getCondition(state, conditionIndex, branchingLogicIndex);

			condition.displayLogicValues = condition.displayLogicValues.filter(
				(val) => values.findIndex((selected) => selected.name === val.valueText) > -1,
			);
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		updateActionType: (
			state,
			{
				payload: { actionType, branchingLogicIndex },
			}: PayloadAction<{
				actionType: ActionType;
				branchingLogicIndex: number;
			}>,
		) => {
			state.branchingLogicEditModels[branchingLogicIndex].displayLogicRule.displayLogicAction.actionType = actionType;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		updateDestinationQuestionId: (
			state,
			{
				payload: { destinationQuestionId, branchingLogicIndex },
			}: PayloadAction<{
				destinationQuestionId?: number;
				branchingLogicIndex: number;
			}>,
		) => {
			state.branchingLogicEditModels[branchingLogicIndex].displayLogicRule.displayLogicAction.destinationQuestionId = destinationQuestionId;
			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
		updateQuestion: (
			state,
			{
				payload: { questionId, questionDisplayOrder, oldQuestionDisplayOrder },
			}: PayloadAction<{ questionId: number; questionDisplayOrder: number; oldQuestionDisplayOrder: number }>,
		) => {
			state.branchingLogicEditModels.forEach((branchingLogicEditModel) => {
				branchingLogicEditModel.displayLogicRule.surveyQuestionId = questionId;

				branchingLogicEditModel.displayLogicConditions = branchingLogicEditModel.displayLogicConditions.filter(
					(displayLogicCondition) =>
						questionDisplayOrder !== 1 ||
						(questionDisplayOrder === 1 && displayLogicCondition.conditionSourceType !== ConditionSourceType.PreviousQuestion),
				);

				branchingLogicEditModel.displayLogicConditions.forEach((displayLogicCondition, index) => {
					displayLogicCondition.index = index;

					const resetCondition =
						!!displayLogicCondition.conditionSourceId &&
						(questionId === +displayLogicCondition.conditionSourceId || questionDisplayOrder < oldQuestionDisplayOrder) &&
						displayLogicCondition.conditionSourceType === ConditionSourceType.PreviousQuestion;
					if (resetCondition) {
						displayLogicCondition.conditionSourceId = undefined;
						displayLogicCondition.conditionSourceText = undefined;
						displayLogicCondition.displayLogicValues = [];
					}
				});
			});

			state.branchingLogicEditModels = state.branchingLogicEditModels.filter(
				(branchingLogicEditModel) => branchingLogicEditModel.displayLogicConditions.length > 0,
			);

			state.unsavedBranchingLogicChanges = true;
			state.validationModels = undefined;
		},
	},
	extraReducers: (builder) => {
		builder
			.addCase(loadQuestionBranchingLogic.fulfilled, (state, action) => {
				const { payload: branchingLogicEditModels } = action;
				const updatedBranchingLogicEditModels =
					branchingLogicEditModels.length !== 0
						? branchingLogicEditModels.map(
								(bl) =>
									({
										displayLogicRule: bl.displayLogicRule,
										displayLogicConditions: bl.displayLogicConditions.map((c, index) => ({
											...c,
											index,
											attributes: [],
										})),
									} as IDisplayLogicEditModel),
						  )
						: [];

				state.isLoadingOrSaving = false;
				state.branchingLogicEditModels = updatedBranchingLogicEditModels;
			})
			.addCase(loadQuestionBranchingLogic.rejected, (state) => {
				state.isLoadingOrSaving = false;
			})
			.addMatcher(
				isAnyOf(loadQuestionBranchingLogic.pending, saveQuestionBranchingLogic.pending, saveGroupQuestionBranchingLogic.pending),
				(state) => {
					state.isLoadingOrSaving = true;
				},
			)
			.addMatcher(isAnyOf(saveQuestionBranchingLogic.rejected, saveGroupQuestionBranchingLogic.rejected), (state, action) => {
				state.isLoadingOrSaving = false;
				state.validationModels = action.payload as IValidationModel<IDisplayLogicEditModel>[];
			});
	},
});

const getCondition = (state: IQuestionBranchingLogicState, conditionIndex: number, branchingLogicIndex: number) => {
	const { branchingLogicEditModels } = state;
	const branchingLogicEditModel = branchingLogicEditModels[branchingLogicIndex];
	return branchingLogicEditModel.displayLogicConditions[conditionIndex];
};

function createNewCondition(conditionIndex: number, conditionSourceType: ConditionSourceType) {
	const condition: IDisplayLogicCondition = {
		index: conditionIndex,
		displayLogicValues: [],
		conditionSourceType: conditionSourceType,
		conditionType: ConditionType.QuestionResponse,
		targetValueOperator: TargetValueOperator.In,
	};

	return condition;
}

const getErrorCondition = (audienceSourceType?: AudienceSourceType) => {
	// for reference: fileupload is other source
	// Hierarchy is Hierarchy structure
	// Hris is employee file
	if (audienceSourceType === undefined || audienceSourceType === null) {
		return DisplayLogicConditionError.NoSourceSelected;
	}
	return undefined;
};

const getPageNumber = (pageFlowElements: IPageFlowElementModel[], questionSequence: number | undefined): number => {
	assertNonNullable(questionSequence);
	return (
		pageFlowElements.filter((pageFlowElement) => {
			assertNonNullable(pageFlowElement.sequence);
			return pageFlowElement.elementType === ElementType.PageBreak && pageFlowElement.sequence < questionSequence;
		}).length + 1
	);
};

type DisplayLogicQuestionFilters = { minDisplayOrder?: number; maxDisplayOrder?: number; hasResponseOptions?: true };

const selectDisplayLogicQuestions = createSelector(
	[
		(state: RootState) => state.survey.surveyEditModel.questions,
		(state: RootState) => state.survey.surveyEditModel.pageFlowElements,
		(_, filters: DisplayLogicQuestionFilters) => filters,
	],
	(surveyQuestions, pageFlowElements, filters): IDisplayLogicQuestion[] =>
		surveyQuestions
			.filter((q) => {
				assertNonNullable(q.displayOrder);

				return (
					(!filters.minDisplayOrder || q.displayOrder > filters.minDisplayOrder) &&
					(!filters.maxDisplayOrder || q.displayOrder < filters.maxDisplayOrder) &&
					(!filters.hasResponseOptions ||
						(q.questionType !== QuestionType.OpenEnded && q.questionType !== QuestionType.TextBox && !!q.responseOptions?.length)) &&
					!q.parentQuestionId
				);
			})
			.map(({ surveyQuestionId, displayOrder, text, responseOptions, isRequired, sequence }): IDisplayLogicQuestion => {
				assertNonNullable(surveyQuestionId);
				assertNonNullable(displayOrder);

				return {
					surveyQuestionId,
					displayOrder,
					text,
					responseOptionGroups: responseOptions ?? [],
					isRequired,
					pageNumber: getPageNumber(pageFlowElements, sequence),
				};
			}),
);

const displayLogicQuestionToSelectOption = ({
	surveyQuestionId,
	pageNumber,
	displayOrder,
	text,
}: IDisplayLogicQuestion): ISelectOption => ({
	displayOrder,
	displayText: `Q${displayOrder}: ${text}`,
	name: surveyQuestionId.toString(),
	group: `Page ${pageNumber}`,
	groupOrder: pageNumber,
});

export {
	displayLogicQuestionToSelectOption,
	getErrorCondition,
	initialState,
	type IQuestionBranchingLogicState,
	loadQuestionBranchingLogic,
	saveGroupQuestionBranchingLogic,
	saveQuestionBranchingLogic,
	selectDisplayLogicQuestions,
};
export const {
	addNewCondition,
	addNewRule,
	deleteCondition,
	attributeChange,
	conditionQuestionChange,
	resetQuestionBranchingLogic,
	conditionResponseChange,
	selectionDismissal,
	updateActionType,
	updateDestinationQuestionId,
	updateQuestion,
} = questionBranchingLogicSlice.actions;
export default questionBranchingLogicSlice.reducer;
